import { AudiencePageLayout, type AudienceContent } from '../audience/AudiencePageLayout';

const content: Record<'de' | 'en', AudienceContent> = {
  de: {
    title: 'qtplan – Squad-Forming: Team-Zusammensetzung als Methode | Skills, PERT & Konflikte',
    metaDesc: 'Team-Zusammensetzung als mathematischer Ansatz statt politischer Kompromiss. Skills, individuelle Schätzungen und Doppelbuchungs-Erkennung in einem Tool.',
    hero: {
      h1: 'Skills definieren. Leute zuordnen. Konfigurationen durchspielen.',
      lead: 'Team-Zusammensetzung als mathematischer Ansatz statt politischer Kompromiss. Skills definieren, Skill-Level pro Person erfassen, individuelle PERT-Schätzungen vergleichen. qtplan zeigt was verschiedene Team-Konfigurationen für Timeline, Risiko und Kosten bedeuten — mit Zahlen statt Bauchgefühl.',
    },
    bento: {
      heading: 'Team-Aufstellung mit System',
      items: [
        {
          span: 2,
          label: 'BOARD',
          title: 'Team-Konfigurationen interaktiv durchspielen',
          body: 'Auf dem Board werden Tasks Personen zugeordnet — und die Auswirkungen sind sofort sichtbar. Andere Zusammensetzung? Anderes Ergebnis. Board und Timeline aktualisieren sich live. Stakeholder können das selbst ausprobieren und alternative Strukturen durchspielen. Skills in Kombination mit individuellen Schätzungen und PERT-Unsicherheiten ergeben ein klares Bild — fast ein mathematischer Beweis dass die Zusammensetzung optimal ist.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'SKILLS',
          title: 'Skill-Matrix mit drei Leveln',
          body: 'Skills werden zentral definiert und jedem Teammitglied in drei Leveln zugeordnet: Grundkenntnisse, Erfahrung, Experte. Preset-Vorlagen ermöglichen schnelles Bootstrapping, die Zuordnung funktioniert per Drag-and-Drop. Tasks können Skill-Anforderungen definieren — der Abgleich zeigt sofort wer einen Task übernehmen kann und wer nicht. Die Team-Coverage-Ansicht zeigt auf einen Blick wo die Abdeckung dünn ist.',
        },
        {
          span: 1,
          label: 'ABGLEICH',
          title: 'Automatischer Skill-Abgleich',
          body: 'Wenn ein Task bestimmte Skills erfordert, zeigt qtplan welche Teammitglieder diese Skills mitbringen — und auf welchem Level. Lücken werden sichtbar bevor sie zum Problem werden. Kein manuelles Prüfen, kein Raten.',
        },
        {
          span: 2,
          label: 'SCHÄTZUNG',
          title: 'Individuelle PERT-Schätzungen pro Person',
          body: 'Derselbe Task dauert bei verschiedenen Leuten unterschiedlich lang. Ein Senior schätzt 2-3-5 Tage, ein Junior 4-7-12. qtplan unterstützt individuelle PERT-Schätzungen pro Teammitglied. Wer den Task bekommt, verändert die Timeline — und diese Veränderung ist sofort sichtbar. Team-Zusammensetzung wird zur bewussten Entscheidung mit klaren Konsequenzen.',
        },
        {
          span: 1,
          label: 'KONFLIKTE',
          title: 'Doppelbuchungen erkennen',
          body: 'Die Scheduling-Engine prüft automatisch ob Teammitglieder in überlappenden Zeiträumen mehrfach gebucht sind — über alle Boards hinweg. Doppelbuchungen werden direkt markiert. Kein manuelles Prüfen ob jemand bereits in einem anderen Projekt eingeplant ist.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'ROADMAP',
          title: 'Auswirkungen in der Timeline sehen',
          body: 'Jede Änderung in der Team-Zusammensetzung wirkt sich sofort auf die Gantt-Timeline aus. Andere Person, andere Schätzung, andere Timeline. Der Vergleich verschiedener Konfigurationen wird greifbar — nicht als abstrakte Diskussion sondern als sichtbare Veränderung.',
        },
        {
          span: 1,
          label: 'SENSIBEL',
          title: 'Teamdaten schützen',
          body: 'Skill-Level, individuelle Schätzungen und Team-Zuordnungen sind sensible Daten — in Kombination geht das in Richtung Keeper\'s Test. qtplan hat einen Modus der individuelle Schätzungen ausblendet für Meetings mit dem Team wo nur das Ergebnis relevant ist. Dazu läuft alles lokal im Browser — keine Cloud, kein Backend. Teamdaten verlassen nie den Rechner und können nicht gegen Teammitglieder verwendet werden.',
        },
        {
          span: 2,
          label: 'NACHVOLLZIEHBAR',
          title: 'Belegen statt behaupten',
          body: 'Wenn die Frage kommt "Warum brauchen wir diesen Senior?" oder "Können wir das Team nicht kleiner machen?" liefert qtplan die Antwort als Zahl. Individuelle Schätzungen, Skill-Abgleich, Timeline-Auswirkungen — alles dokumentiert, alles nachvollziehbar. Team-Entscheidungen werden von politischen Kompromissen zu fundierten Entscheidungen.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Warum Team-Aufstellung mehr braucht als Verfügbarkeit',
      paragraphs: [
        'In den meisten Organisationen basiert die Team-Zusammensetzung auf Verfügbarkeit: Wer ist gerade frei? Das Ergebnis sind Teams die zufällig zusammengesetzt sind statt für das Projekt optimiert. Skills werden ignoriert, individuelle Stärken nicht genutzt, Risiken durch falsche Besetzung nicht erkannt.',
        'qtplan macht Team-Zusammensetzung zu einem methodischen Prozess mit klaren Schritten: Skills definieren die das Projekt braucht. Leute zuordnen und sehen ob die Skills abgedeckt sind. PERT-Schätzungen pro Person zeigen wer den Task wie schnell schaffen kann. Die Timeline zeigt die Gesamtdauer mit dieser Teamkonstellation. Divergierende Schätzungen zeigen wo Unsicherheit und Wissenslücken bestehen — bevor das Projekt startet, nicht wenn die erste Aufgabe scheitert.',
        'Dazu kommen individuelle PERT-Schätzungen. Derselbe Task dauert bei verschiedenen Leuten unterschiedlich lang. Wer den Task bekommt, beeinflusst die Timeline — und das sollte eine bewusste Entscheidung sein. qtplan macht diese Entscheidung sichtbar: andere Person, andere Schätzung, andere Timeline. Live, auf dem Board, in der Roadmap.',
        'Und wenn die Frage kommt ob das Team kleiner sein kann: qtplan liefert die Antwort als Zahl. Nicht als Gefühl, nicht als politischer Kompromiss, sondern als nachvollziehbare Auswirkung auf Timeline und Risiko. Doppelbuchungs-Erkennung über alle Boards hinweg zeigt wo Personen bereits eingeplant sind. Team-Entscheidungen werden fundiert statt zufällig.',
      ],
    },
    cta: {
      heading: 'Skills definieren, Team zusammenstellen',
      body: 'Kein Account. Keine Cloud. Alle Daten bleiben lokal im Browser. Skills anlegen, Teammitglieder erfassen und erleben wie verschiedene Konfigurationen die Timeline verändern.',
    },
  },
  en: {
    title: 'qtplan – Squad Forming: Team Composition as Method | Skills, PERT & Conflicts',
    metaDesc: 'Team composition as a mathematical approach instead of political compromise. Skills, individual estimates, and double-booking detection in one tool.',
    hero: {
      h1: 'Define skills. Assign people. Explore configurations.',
      lead: 'Team composition as a mathematical approach instead of political compromise. Define skills, capture skill levels per person, compare individual PERT estimates. qtplan shows what different team configurations mean for timeline, risk, and cost — with numbers instead of gut feeling.',
    },
    bento: {
      heading: 'Team Assembly with System',
      items: [
        {
          span: 2,
          label: 'BOARD',
          title: 'Explore Team Configurations Interactively',
          body: 'On the board, tasks are assigned to people — and the effects are immediately visible. Different composition? Different result. Board and timeline update live. Stakeholders can try this themselves and explore alternative structures. Skills combined with individual estimates and PERT uncertainties produce a clear picture — almost a mathematical proof that the composition is optimal.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'SKILLS',
          title: 'Skill Matrix with Three Levels',
          body: 'Skills are defined centrally and assigned to each team member in three levels: basic knowledge, experience, expert. Preset templates enable quick bootstrapping, assignment works via drag-and-drop. Tasks can define skill requirements — the matching shows immediately who can take on a task and who cannot. The team coverage view shows at a glance where coverage is thin.',
        },
        {
          span: 1,
          label: 'MATCHING',
          title: 'Automatic Skill Matching',
          body: 'When a task requires certain skills, qtplan shows which team members have those skills — and at what level. Gaps become visible before they become problems. No manual checking, no guessing.',
        },
        {
          span: 2,
          label: 'ESTIMATION',
          title: 'Individual PERT Estimates per Person',
          body: 'The same task takes different people different amounts of time. A senior estimates 2-3-5 days, a junior 4-7-12. qtplan supports individual PERT estimates per team member. Who gets the task changes the timeline — and that change is immediately visible. Team composition becomes a conscious decision with clear consequences.',
        },
        {
          span: 1,
          label: 'CONFLICTS',
          title: 'Detect Double Bookings',
          body: 'The scheduling engine automatically checks whether team members are booked in overlapping time periods — across all boards. Double bookings are flagged directly. No manual checking whether someone is already scheduled in another project.',
          variant: 'accent',
        },
        {
          span: 1,
          label: 'ROADMAP',
          title: 'See Effects in the Timeline',
          body: 'Every change in team composition immediately affects the Gantt timeline. Different person, different estimate, different timeline. Comparing different configurations becomes tangible — not as an abstract discussion but as a visible change.',
        },
        {
          span: 1,
          label: 'SENSITIVE',
          title: 'Protect Team Data',
          body: 'Skill levels, individual estimates, and team assignments are sensitive data — combined, this moves toward a keeper\'s test. qtplan has a mode that hides individual estimates for meetings with the team where only the result matters. On top of that, everything runs locally in the browser — no cloud, no backend. Team data never leaves the machine and cannot be used against team members.',
        },
        {
          span: 2,
          label: 'TRACEABLE',
          title: 'Substantiate Instead of Assert',
          body: 'When the question comes "Why do we need this senior?" or "Can we make the team smaller?" qtplan provides the answer as a number. Individual estimates, skill matching, timeline impact — all documented, all traceable. Team decisions move from political compromises to informed decisions.',
          variant: 'muted',
        },
      ],
    },
    narrative: {
      heading: 'Why Team Assembly Needs More Than Availability',
      paragraphs: [
        'In most organizations, team composition is based on availability: Who is currently free? The result is teams that are randomly assembled rather than optimized for the project. Skills are ignored, individual strengths are not utilized, and risks from wrong staffing are not recognized.',
        'qtplan makes team composition a methodical process with clear steps: define the skills the project needs. Assign people and check if skills are covered. Per-person PERT estimates show who can complete the task how quickly. The timeline shows the total duration with this team configuration. Diverging estimates reveal where uncertainty and knowledge gaps exist — before the project starts, not when the first task fails.',
        'On top of that come individual PERT estimates. The same task takes different people different amounts of time. Who gets the task affects the timeline — and that should be a conscious decision. qtplan makes this decision visible: different person, different estimate, different timeline. Live, on the board, in the roadmap.',
        'And when the question comes whether the team can be smaller: qtplan provides the answer as a number. Not as a feeling, not as a political compromise, but as a traceable impact on timeline and risk. Double-booking detection across all boards shows where people are already scheduled. Team decisions become informed rather than random.',
      ],
    },
    cta: {
      heading: 'Define skills, assemble the team',
      body: 'No account. No cloud. All data stays local in the browser. Create skills, add team members, and experience how different configurations change the timeline.',
    },
  },
};

export function SquadFormingPage({ lang }: { lang: 'de' | 'en' }) {
  return <AudiencePageLayout lang={lang} slug="squad-forming" content={content[lang]} basePath="features" />;
}
